// Measures the fixed top/bottom chrome and publishes its height as CSS custom
// properties on <html>, so every other fixed-position surface can position off
// the real bar instead of a guess.
//
// Before this, six rules hardcoded `top: 56px` / `bottom: 110px` / `bottom: 200px`
// and one media query re-hardcoded `bottom: 124px`. All of them were wrong in at
// least one configuration: the topbar is 68px tall, so `top: 56px` put the sun and
// material panels *underneath* it, and the bottom bar grows past 110px as soon as
// the variant chips wrap to a second row.
//
// The bars are `display: none` while `.hidden` (pre-boot, and in XR). A measured
// height of 0 would then collapse every panel onto the viewport edge, so a zero
// reading keeps the last good value instead.

const TOPBAR_FALLBACK_PX = 68;
const BOTTOMBAR_FALLBACK_PX = 104;

interface Tracked {
  el: HTMLElement;
  prop: string;
  last: number;
}

export class ChromeMetrics {
  private observer: ResizeObserver | null = null;
  private readonly tracked: Tracked[] = [];
  private readonly onWindowResize = () => this.measureAll();

  install(): void {
    this.track('topbar', '--topbar-h', TOPBAR_FALLBACK_PX);
    this.track('bottombar', '--bottombar-h', BOTTOMBAR_FALLBACK_PX);
    if (this.tracked.length === 0) return;

    this.measureAll();

    // ResizeObserver catches the cases a resize listener misses: the variant
    // chips wrapping to a second row, a room title growing, the menu rail
    // repartitioning. Both are needed — RO does not fire on a viewport-only
    // change that leaves the box identical but moves the safe-area insets.
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measureAll());
      for (const t of this.tracked) this.observer.observe(t.el);
    }
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('orientationchange', this.onWindowResize);
  }

  /**
   * Track an element that does not exist yet at install() time.
   *
   * The edit-mode toolbar is built by EditorMode.attach(), which runs well
   * after App wires the chrome, so install() would log a warning and skip it.
   * Without a measured height every surface that has to clear the toolbar is
   * back to guessing — which is exactly how `body.is-editing .menu-sheet` got a
   * hardcoded 52px that stopped being true the moment a second slider pushed
   * the toolbar onto a second row.
   *
   * Idempotent per property, so an editor that re-attaches cannot stack
   * duplicate observers on the same box.
   */
  trackLate(id: string, prop: string, fallback: number): void {
    if (this.tracked.some((t) => t.prop === prop)) return;
    const before = this.tracked.length;
    this.track(id, prop, fallback);
    const added = this.tracked[this.tracked.length - 1];
    if (this.tracked.length === before || !added) return;
    this.observer?.observe(added.el);
    this.measureAll();
  }

  destroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('orientationchange', this.onWindowResize);
    this.tracked.length = 0;
  }

  private track(id: string, prop: string, fallback: number): void {
    const el = document.getElementById(id);
    if (!el) {
      console.warn(`[chromeMetrics] #${id} missing — ${prop} keeps its CSS default`);
      return;
    }
    this.tracked.push({ el, prop, last: fallback });
  }

  private measureAll(): void {
    const root = document.documentElement;
    for (const t of this.tracked) {
      const h = Math.round(t.el.getBoundingClientRect().height);
      // 0 means hidden, not "no chrome" — keep the last good height.
      if (h > 0) t.last = h;
      root.style.setProperty(t.prop, `${t.last}px`);
    }
  }
}
